import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getFullImageUrl } from "../api";
import { getFallbackImage } from "../utils/imageLoader";
import './OrderHistory.css';
import { Modal } from "react-bootstrap";
import { FaChevronRight } from "react-icons/fa";

const OrderHistoryPage = () => {

    // State variable
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxSrc, setLightboxSrc] = useState(null);

    // Check loged in or not based on local storage
    useEffect(() => {
        const fetchOrders = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }
            setLoading(true);

            // fetching all orders
            try {
                const data = await apiFetch('/api/orders/all');
                const list = data.orders || data.data || [];
                // Sort newest first
                list.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
                // Payment confirmation logic
                const isOrderVisible = (order) => {
                    const paymentStatus = order.payment && order.payment.status ? String(order.payment.status).toLowerCase() : '';
                    const orderStatus = order.status ? String(order.status).toLowerCase() : (order.orderStatus ? String(order.orderStatus).toLowerCase() : '');

                    // if payment succes, order is visible
                    if (paymentStatus === 'success' || paymentStatus === 'paid') return true;
                    const visibleStatuses = ['confirmed', 'processing', 'shipped', 'delivered', 'completed'];
                    if (visibleStatuses.includes(orderStatus)) return true;

                    return false;
                };

                const filtered = list.filter(isOrderVisible);
                setOrders(filtered);
            } catch (err) {
                setError(err.message || 'Failed to fetch orders');
            } finally {
                setLoading(false);
            }
        };
        fetchOrders();
    }, [navigate]);

    // geting image 
    const getImageForOrder = (order) => {
        const firstItems = order.orderItems || [];
        if (firstItems.length === 0) return getFallbackImage();
        const first = firstItems[0];
        const prod = first.product || {};
        const candidates = [prod?.images?.[0]?.url, prod?.images?.[0], prod?.imageUrl, prod?.image, first?.imageUrl, first?.image];
        const found = candidates.find(c => !!c);
        return getFullImageUrl(found) || getFallbackImage();
    };

    return (
        <div className="min-vh-100 bg-light">
            <div className="container py-5">
                <div className="text-center mb-5">
                    <h1 className="fw-bold text-dark display-5 mb-3">Order History</h1>
                    <p className="text-muted lead">Track and manage all your orders in one place</p>
                </div>

                {error && (
                    <div className="alert alert-danger">{error}</div>
                )}

                <div className="table-responsive">
                    {loading && (
                        <div className="text-center py-5 w-100">
                            <div className="spinner-border text-purple" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="text-muted mt-3">Loading your orders...</p>
                        </div>
                    )}

                    {!loading && orders.length === 0 && (
                        <div className="text-center py-5 w-100">
                            <div className="card border-0 bg-light py-5">
                                <div className="card-body">
                                    <div className="display-1 text-muted mb-3">📦</div>
                                    <h4 className="text-muted mb-3">No orders found</h4>
                                    <p className="text-muted mb-4">You haven't placed any orders yet.</p>
                                    <Link to="/products" className="btn btn-purple btn-lg">Start Shopping</Link>
                                </div>
                            </div>
                        </div>
                    )}

                    {!loading && orders.length > 0 && (
                        <table className="table align-middle order-table bg-white shadow-sm">
                            <thead>
                                <tr>
                                    <th style={{width: '110px'}}>Item</th>
                                    <th>Product</th>
                                    <th className="d-none d-md-table-cell">Date</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                    <th style={{width: '140px'}}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map(order => (
                                    <tr key={order._id}>
                                        <td>
                                            <img
                                                src={getImageForOrder(order)}
                                                alt={`Order ${order._id}`}
                                                className="order-thumb rounded"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    try {
                                                        const src = getImageForOrder(order);
                                                        setLightboxSrc(src);
                                                        setLightboxOpen(true);
                                                    } catch (err) {
                                                        console.error('Failed to open image viewer', err);
                                                    }
                                                }}
                                            />
                                        </td>
                                        <td>
                                            {(() => {
                                                const firstItems = order.orderItems || [];
                                                const first = firstItems[0] || null;
                                                const prod = first ? (first.product || {}) : {};
                                                const prodName = prod?.name || prod?.title || first?.title || "Product Item";
                                                return (
                                                    <>
                                                        <div className="fw-bold text-truncate-1">{prodName}</div>
                                                        <div className="text-muted small">{(order.orderItems && order.orderItems.length) ? `${order.orderItems.length} item(s)` : '0 items'}</div>
                                                        <div className="text-muted small d-md-none">{new Date(order.createdAt).toLocaleString()}</div>
                                                    </>
                                                );
                                            })()}
                                        </td>
                                        <td className="text-muted small d-none d-md-table-cell">{new Date(order.createdAt).toLocaleString()}</td>
                                        <td className="fw-bold">₹{order.total_amount ? order.total_amount.toFixed(2) : '0.00'}</td>
                                                <td>
                                                    {
                                                        (() => {
                                                            // Use same logic as OrderTracking to derive status
                                                            const currentStatus = order.status ? String(order.status).toLowerCase() : 'pending';
                                                            const paymentStatus = order.payment?.status ? String(order.payment.status) : 'Pending';
                                                            const display = (order.status && String(order.status).toUpperCase()) || (order.orderStatus && String(order.orderStatus).toUpperCase()) || (paymentStatus && String(paymentStatus).toUpperCase()) || 'PENDING';

                                                            // Decide badge color similar to tracking view
                                                            let bgColor = '#f0ad4e'; // warning by default
                                                            if (currentStatus === 'cancelled' || currentStatus === 'refunded') bgColor = '#dc3545';
                                                            else if (currentStatus === 'delivered') bgColor = '#198754';
                                                            else if (currentStatus === 'confirmed') bgColor = '#0d6efd';

                                                            return (
                                                                <span className="status-badge" style={{ backgroundColor: bgColor, color: '#fff' }}>
                                                                    {display}
                                                                </span>
                                                            );
                                                        })()
                                                    }
                                                </td>
                                        <td>
                                            <div className="d-flex gap-2 justify-content-end">
                                                <Link to={`/ordertracking/${order._id}`} className="btn btn-outline-purple btn-sm d-inline-flex align-items-center">
                                                    View Details <FaChevronRight className="ms-2" />
                                                </Link>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
            <Modal show={lightboxOpen} onHide={() => setLightboxOpen(false)} centered size="lg">
                <Modal.Header closeButton style={{ border: 'none' }} />
                <Modal.Body className="p-0 d-flex justify-content-center align-items-center" style={{ background: '#000' }}>
                    <img src={lightboxSrc || ''} alt="" className="img-fluid" style={{ maxHeight: '80vh', maxWidth: '100%' }} onError={(e) => { e.target.src = getFallbackImage(); }} />
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default OrderHistoryPage;